import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import UserRow from '../rowComponents/UserRow';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  Paper,
  Skeleton,
  Box
} from '@mui/material';
const initialFilters = {};

function UsersTable({ users = [], isLoading, onAction, userReducer }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [filters, setFilters] = useState(initialFilters);
  const [searchText, setSearchText] = useState("");

  const isAdmin = userReducer?.userType?.toLowerCase() === 'admin';

  useEffect(() => {
    const totalRecords = users.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [users, pageSize, currentPage]);

  const goToPage = (page) => {
    const totalRecords = users.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    const safePage = Math.min(Math.max(page, 1), totalPages);
    if (safePage !== currentPage) {
      setCurrentPage(safePage);
    }
  };

  const goToNextPage = () => {
    goToPage(currentPage + 1);
  };

  const goToPreviousPage = () => {
    goToPage(currentPage - 1);
  };

  const handlePageSizeChange = (e) => {
    const value = parseInt(e.target.value, 10) || 10;
    setPageSize(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setFilters(initialFilters);
    setSearchText("");
    setCurrentPage(1);
  };

  const hasFilterValues = Object.values(filters).some((val) => (val || "").trim() !== "");
  const hasSearchText = !!(searchText && searchText.trim());
  const anythingFiltered = hasFilterValues || hasSearchText;

  // --- Sorting ---
  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
    setCurrentPage(1);
  };

  const getSortableValue = (user, key) => {
    switch (key) {
      case "username":
        return (user.username || "").toLowerCase();
      case "name":
        return (user.name || "").toLowerCase();
      case "email":
        return (user.email || "").toLowerCase();
      case "status":
        return user.isActive ? 1 : 0;
      case "type":
        return (user.type || "").toLowerCase();
      case "groups":
        return (user.groupIds || []).join(",").toLowerCase();
      default:
        return "";
    }
  };

  const SortHeader = ({ label, sortKey, style, align = 'left' }) => {
    const active = sortConfig.key === sortKey;
    const direction = active ? sortConfig.direction : 'asc';

    return (
      <TableCell style={style} align={align}>
        <TableSortLabel
          active={active}
          direction={direction}
          onClick={() => requestSort(sortKey)}
        >
          {label}
        </TableSortLabel>
      </TableCell>
    );
  };

  // --- Filter (search) ---
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      if (!searchText || !searchText.trim()) return true;
      const term = searchText.toLowerCase();

      return (
        (user.username || "").toLowerCase().includes(term) ||
        (user.folder || "").toLowerCase().includes(term) ||
        (user.isActive ? "active" : "inactive").includes(term) ||
        (user.type || "").toLowerCase().includes(term) ||
        (user.groupIds || []).some((g) => (g || "").toLowerCase().includes(term))
      );
    });
  }, [users, searchText]);

  // --- Sort (needs to be before pagination) ---
  const sortedUsers = useMemo(() => {
    const arr = [...filteredUsers];
    if (!sortConfig.key) return arr;

    arr.sort((a, b) => {
      const av = getSortableValue(a, sortConfig.key);
      const bv = getSortableValue(b, sortConfig.key);

      if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
      if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return arr;
  }, [filteredUsers, sortConfig]);

  // --- Pagination (after sort) ---
  const totalRecords = sortedUsers.length;
  const totalPages = totalRecords === 0 ? 1 : Math.ceil(totalRecords / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalRecords);
  const pageUsers = sortedUsers.slice(startIndex, endIndex);

  return (
    <div className="s-table-center">
      <div className="s-table-toolbar">
        <div className="s-table-toolbar-right">
          <span className="s-table-record-count">
            {isLoading
              ? "Loading users..." 
              : totalRecords === 0
              ? "No users"
              : `Showing ${totalRecords === 0 ? 0 : startIndex + 1}–${endIndex} of ${totalRecords} users`}
              
          </span>

          <div className="s-table-page-size">
            <span>Rows per page:</span>
            <select value={pageSize} onChange={handlePageSizeChange}>
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={150}>150</option>
              <option value={500}>500</option>
            </select>
          </div>

          <div className="s-table-pagination">
            <button
              type="button"
              className="s-table-page-btn"
              onClick={goToPreviousPage}
              disabled={isLoading || totalRecords === 0 || currentPage === 1}
            >
              ‹ Prev
            </button>

            <span className="s-table-page-info">
              Page {totalRecords === 0 ? 0 : currentPage} of {totalRecords === 0 ? 0 : totalPages}
            </span>

            <button
              type="button"
              className="s-table-page-btn"
              onClick={goToNextPage}
              disabled={isLoading || totalRecords === 0 || currentPage >= totalPages}
            >
              Next ›
            </button>
          </div>
        </div>

        <div className="s-table-toolbar-left">
          {anythingFiltered && (
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={clearAllFilters}
              title="Clear all filter fields"
              style={{ marginLeft: "10px" }}
            >
              <i className="fa fa-times" aria-hidden="true" style={{ marginRight: "5px" }}></i>
              Clear Filters
            </button>
          )}

          <input
            type="text"
            className="s-table-search"
            placeholder="Search users..."
            value={searchText || ""}
            onChange={handleSearchChange}
            style={{ marginLeft: "10px" }}
          />
        </div>
      </div>

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eee' }}>
        <Table className="s-table s-table-users">
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell align="center" sx={{ width: 50 }}></TableCell>
              <TableCell align="center" sx={{ width: 50 }}>Edit</TableCell>
              <TableCell align="center" sx={{ width: 50 }}>Delete</TableCell>

              <SortHeader label="Username" sortKey="username" />
              <SortHeader label="Name" sortKey="name" />
              <SortHeader label="Email" sortKey="email" />
              <SortHeader label="Status" sortKey="status" style={{ maxWidth: 120 }} />
              <SortHeader label="Type" sortKey="type" style={{ maxWidth: 100 }} />
              <SortHeader label="Groups" sortKey="groups" style={{ minWidth: 150 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              // Skeleton loading rows
              [...Array(pageSize)].map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  <TableCell colSpan={10}>
                    <Skeleton variant="rectangular" height={40} />
                  </TableCell>
                </TableRow>
              ))
            ) : pageUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Box sx={{ color: 'text.secondary' }}>
                    No users found.
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              pageUsers.map((u) => {
                const id = u.id || u._id || u.username;
                return <UserRow key={id} user={u} onAction={onAction} />;
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

export default connect((state) => state)(UsersTable);